import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';

import * as umParticipantActions from './um-participant.actions';
import { UmParticipant } from '../../../models/um-participant';
import * as metricsActions from '../metrics/metrics.actions';

export interface UmParticipantState extends EntityState<UmParticipant> {
  loadingUmParticipants: boolean;
  umParticipantsLoaded: boolean;
  total: number;
  loadingSelectedUmParticipant: boolean;
  selectedUmParticipantLoaded: boolean;
  selectedUmParticipant: UmParticipant;
  downloadingParticipantHistory: boolean;
  participantHistoryDownloaded: boolean;
  downloadingParticipantData: boolean;
  participantDataDownloaded: boolean;
}

export const umParticipantAdapter: EntityAdapter<UmParticipant> = createEntityAdapter<UmParticipant>({});

export const initialUmParticipantsState: UmParticipantState = umParticipantAdapter.getInitialState({
  loadingUmParticipants: false,
  umParticipantsLoaded: false,
  total: 0,
  loadingSelectedUmParticipant: false,
  selectedUmParticipantLoaded: false,
  selectedUmParticipant: null,
  downloadingParticipantHistory: false,
  participantHistoryDownloaded: false,
  downloadingParticipantData: false,
  participantDataDownloaded: false,
});

export const reducer = createReducer(
  initialUmParticipantsState,
  on(umParticipantActions.getUmParticipants, (state) => {
    return umParticipantAdapter.removeAll({
      ...state,
      loadingUmParticipants: true,
      umParticipantsLoaded: false,
    });
  }),
  on(umParticipantActions.getUmParticipantsSuccess, (state, { umParticipantResponse }) => {
    return umParticipantAdapter.setAll(umParticipantResponse?.data, {
      ...state,
      loadingUmParticipants: false,
      umParticipantsLoaded: true,
      total: umParticipantResponse?.total,
    });
  }),
  on(umParticipantActions.getUmParticipantsError, (state) => {
    return {
      ...state,
      loadingUmParticipants: false,
      umParticipantsLoaded: false,
    };
  }),
  on(umParticipantActions.selectUmParticipant, (state, { participantId }) => {
    return {
      ...state,
      loadingSelectedUmParticipant: true,
      selectedUmParticipantLoaded: false,
    };
  }),
  on(umParticipantActions.selectUmParticipantSuccess, (state, { selectedUmParticipant }) => {
    return {
      ...state,
      loadingSelectedUmParticipant: false,
      selectedUmParticipantLoaded: true,
      selectedUmParticipant,
    };
  }),
  on(umParticipantActions.selectUmParticipantError, (state) => {
    return {
      ...state,
      loadingSelectedUmParticipant: false,
      selectedUmParticipantLoaded: false,
    };
  }),
  on(umParticipantActions.withdrawParticipantFromStudyAndKeepDataSuccess, (state, { umParticipant }) => {
    return {
      ...state,
      loadingSelectedUmParticipant: false,
      selectedUmParticipantLoaded: true,
      selectedUmParticipant: umParticipant,
    };
  }),
  on(umParticipantActions.withdrawParticipantFromStudyAndRemoveDataSuccess, (state, { umParticipant }) => {
    return {
      ...state,
      loadingSelectedUmParticipant: false,
      selectedUmParticipantLoaded: true,
      selectedUmParticipant: umParticipant,
    };
  }),
  on(umParticipantActions.withdrawParticipantFromStudyAndKeepDataAnonymousSuccess, (state, { umParticipant }) => {
    return {
      ...state,
      loadingSelectedUmParticipant: false,
      selectedUmParticipantLoaded: true,
      selectedUmParticipant: umParticipant,
    };
  }),
  on(umParticipantActions.downloadParticipantHistory, (state) => {
    return {
      ...state,
      downloadingParticipantHistory: true,
      participantHistoryDownloaded: false,
    };
  }),
  on(umParticipantActions.downloadParticipantHistorySuccess, (state) => {
    return {
      ...state,
      downloadingParticipantHistory: false,
      participantHistoryDownloaded: true,
    };
  }),
  on(umParticipantActions.downloadParticipantHistoryError, (state) => {
    return {
      ...state,
      downloadingParticipantHistory: false,
      participantHistoryDownloaded: false,
    };
  }),
  on(umParticipantActions.downloadStudyAnswers, (state) => {
    return {
      ...state,
      downloadingParticipantData: true,
      participantDataDownloaded: false,
    };
  }),
  on(umParticipantActions.downloadStudyAnswersSuccess, (state) => {
    return {
      ...state,
      downloadingParticipantHistory: false,
      participantDataDownloaded: true,
    };
  }),
  on(umParticipantActions.downloadStudyAnswersError, (state) => {
    return {
      ...state,
      downloadingParticipantHistory: false,
      participantDataDownloaded: false,
    };
  }),
  on(umParticipantActions.downloadParticipantData, (state) => {
    return {
      ...state,
      downloadingParticipantData: true,
      participantDataDownloaded: false,
    };
  }),
  on(umParticipantActions.downloadParticipantDataSuccess, (state) => {
    return {
      ...state,
      downloadingParticipantData: false,
      participantDataDownloaded: true,
    };
  }),
  on(umParticipantActions.downloadParticipantDataError, (state) => {
    return {
      ...state,
      downloadingParticipantData: false,
      participantDataDownloaded: false,
    };
  })
);

export function umParticipantReducer(state: UmParticipantState | undefined, action: Action) {
  return reducer(state, action);
}

export const { selectIds, selectEntities, selectAll, selectTotal } = umParticipantAdapter.getSelectors();
